import {
  RECEIVE_IMAGES_URLS,
  UPDATE_EVENT_IMAGE_DESCRIPTION,
  DRAG_EVENT_IMAGE,
  REMOVE_EVENT_IMAGE,
  REMOVE_EVENT_IMAGES,
} from '../../constants/ActionTypes';
import editEventContent from '../../functions/event/editEventContent';
import { findEvent, copyQuest, touchQuestModified } from '../questsHelpers';
import eventVideoStickerReducer from './eventVideoStickerReducer';

/**
 * Handles media-related event actions: images, videos, and stickers.
 * @param {Object} state - current state of quests slice
 * @param {Object} action - dispatched Redux action
 * @returns {Object|null}
 */
export default function eventMediaReducer(state, action) {
  const videoStickerState = eventVideoStickerReducer(state, action);
  if (videoStickerState !== null) {
    return videoStickerState;
  }

  let nextQuest;

  switch (action.type) {
    case RECEIVE_IMAGES_URLS: {
      const { imageId } = action;
      nextQuest = copyQuest(state, action.questId);
      if (!nextQuest) return state;
      const event = findEvent(nextQuest.events, action.eventId);
      if (event) {
        event.content = editEventContent(event.content, 'image', (images) => {
          const index =
            images.data.length > imageId ? imageId : images.data.length;
          const newDesc = action.images.map(() => '');
          images.data.splice(index, 0, ...action.images);
          if (images.captions) {
            images.captions.splice(index, 0, ...newDesc);
          }
          return images;
        });
      }

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case UPDATE_EVENT_IMAGE_DESCRIPTION: {
      nextQuest = copyQuest(state, action.questId);
      if (!nextQuest) return state;
      const event = findEvent(nextQuest.events, action.eventId);
      if (event && event.content) {
        event.content = editEventContent(event.content, 'image', (images) => {
          const updatedImages = { ...images };
          if (
            !Object.prototype.hasOwnProperty.call(updatedImages, 'captions')
          ) {
            updatedImages.captions = [];
          }
          if (
            updatedImages.data &&
            updatedImages.data.length !== updatedImages.captions.length
          ) {
            const nextCaptions = [...(updatedImages.captions || [])];
            for (
              let i = updatedImages.captions.length;
              i < updatedImages.data.length;
              i += 1
            ) {
              nextCaptions.push('');
            }
            updatedImages.captions = nextCaptions;
          }
          const finalCaptions = [...(updatedImages.captions || [])];
          finalCaptions[action.imageId] = String(action.description);
          updatedImages.captions = finalCaptions;

          return updatedImages;
        });
      }

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case DRAG_EVENT_IMAGE: {
      const { imageId, newImageId, isUpload } = action;
      nextQuest = copyQuest(state, action.questId);
      if (!nextQuest) return state;
      const event = findEvent(nextQuest.events, action.eventId);
      if (event && event.content && !isUpload) {
        event.content = editEventContent(event.content, 'image', (images) => {
          const { length } = images.data;

          if (length >= newImageId && length > imageId) {
            const tmpImage = images.data.splice(imageId, 1)[0];
            images.data.splice(newImageId, 0, tmpImage);
            if (images.captions) {
              const impDesc = images.captions.splice(imageId, 1)[0];
              images.captions.splice(newImageId, 0, impDesc);
            }
          }

          return images;
        });
      }

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case REMOVE_EVENT_IMAGE: {
      nextQuest = copyQuest(state, action.questId);
      if (!nextQuest) return state;
      const event = findEvent(nextQuest.events, action.eventId);
      if (event && event.content) {
        event.content = editEventContent(
          event.content,
          'image',
          ({ data, captions, ...images }) => ({
            ...images,
            data: data.filter((url, i) => i !== action.imageId),
            captions: (captions || []).filter(
              (desc, i) => i !== action.imageId
            ),
          })
        );
      }

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    case REMOVE_EVENT_IMAGES: {
      nextQuest = copyQuest(state, action.questId);
      if (!nextQuest) return state;
      const event = findEvent(nextQuest.events, action.eventId);
      if (event && event.content) {
        event.content = editEventContent(
          event.content,
          'image',
          ({ data, captions, ...images }) => {
            const { imageIds } = action;
            return {
              ...images,
              data: data.filter((url, i) => !imageIds.includes(i)),
              captions: (captions || []).filter(
                (desc, i) => !imageIds.includes(i)
              ),
            };
          }
        );
      }

      touchQuestModified(nextQuest);

      return {
        ...state,
        quests: state.quests.map((quest) =>
          quest.id === action.questId ? nextQuest : quest
        ),
      };
    }

    default:
      return null;
  }
}
